import React, { useId, useRef, useState } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import type { WheelEntry } from '../types/wheel';
import {
  createImageEntries,
  IMAGE_FILE_ACCEPT,
} from '../utils/imageEntries';
import { useLang } from '../utils/i18n';

export interface ImageUploadButtonProps {
  onAddEntries: (entries: WheelEntry[]) => void;
  disabled?: boolean;
  remainingEntries: number;
  existingImageDataLength: number;
  /** Icon-only presentation for narrow entry panels. */
  compact?: boolean;
  className?: string;
}

const ImageUploadButton: React.FC<ImageUploadButtonProps> = ({
  onAddEntries,
  disabled = false,
  remainingEntries,
  existingImageDataLength,
  compact = false,
  className = '',
}) => {
  const { t } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  const statusId = useId();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const atEntryLimit = remainingEntries <= 0;
  const unavailable = disabled || busy || atEntryLimit;

  const handleFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    // Clear before awaiting so choosing the same file again always emits change.
    event.currentTarget.value = '';
    if (files.length === 0) return;

    setBusy(true);
    setStatus(t('images.preparing'));
    try {
      const result = await createImageEntries(files, {
        remainingEntries,
        existingImageDataLength,
      });
      if (result.entries.length > 0) onAddEntries(result.entries);
      setStatus(result.entries.length > 0
        ? t('import.imported', { n: result.entries.length })
        : t('images.processError'));
    } catch {
      setStatus(t('images.processError'));
    } finally {
      setBusy(false);
    }
  };

  const label = busy ? t('images.preparing') : t('images.upload');
  const title = atEntryLimit ? t('images.entryLimit') : label;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_FILE_ACCEPT}
        multiple
        hidden
        disabled={unavailable}
        onChange={handleFiles}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={unavailable}
        aria-label={label}
        aria-describedby={status ? statusId : undefined}
        aria-busy={busy}
        title={title}
        className={`inline-flex items-center justify-center gap-1.5 rounded-lg bg-violet-50 px-2.5 py-2 text-xs font-semibold text-violet-600 transition-colors hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-violet-900/20 dark:text-violet-400 dark:hover:bg-violet-900/40 ${className}`}
      >
        {busy
          ? <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          : <ImagePlus size={16} aria-hidden="true" />}
        {!compact && <span>{t('images.upload')}</span>}
      </button>
      <span id={statusId} role="status" aria-live="polite" className="sr-only">
        {status}
      </span>
    </>
  );
};

export default ImageUploadButton;
